import { FlexColumn, FlexRowCenterAligned } from '@fox/layout';
import MuiAutocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import React, { ReactNode, useContext, useState } from 'react';
import styled from 'styled-components';
import { Chip } from '../chip/index';
import { Divider } from '../divider/index';
import { HighlightedText } from '../highlighted-text/index';
import { IconButton } from '../icon-button/index';
import { useMuiClickToDismiss } from '../mui-click-to-dismiss/index';
import { ListItemIcon, ListItemText, MuiMenuItem } from '../mui-list/index';
import { ClickToDismissPortalContext, ContainerContext } from '../popover/context';
import { mergeEndAdornments } from '../select/index';
import { ExtendedTextFieldProps, TextField, TextFieldStatus } from '../text-field/index';

export interface TreeSelectItemProps {
	label: string;
	children?: TreeSelectItemProps[];
	selectable?: boolean;
	value?: string;
	/**
	 * Optional: If set, renders this ReactNode on the right of the label for category/header nodes.
	 */
	categoryActionElement?: React.ReactNode;
	/** Optional hex colour for root-level options. Renders a colour dot in the dropdown. */
	colour?: string;
}

export interface TreeSelectOption {
	props: TreeSelectItemProps;
	nestLevel: number;
	collapsed: boolean;
	parentIndex?: number;
	value?: string;
}

export interface TreeSelectProps<
	Multiple extends boolean | undefined = undefined,
	DisableClearable extends boolean | undefined = undefined,
	FreeSolo extends boolean | undefined = undefined
> extends Omit<AutocompleteProps<TreeSelectOption, Multiple, DisableClearable, FreeSolo>, 'renderInput' | 'options'>,
		Pick<ExtendedTextFieldProps, 'label' | 'helperText' | 'startAdornment' | 'required'> {
	variant?: 'standard' | 'cell';
	status?: TextFieldStatus | 'loading';
	options: TreeSelectItemProps[];
	getOptionLabel?: (option: TreeSelectOption) => string;
}

const StyledTextField = styled(TextField)`
	& span.muiautocomplete-tag {
		overflow: visible;
	}
`;

const GridCellTextField = styled(StyledTextField)`
	& .MuiOutlinedInput-notchedOutline {
		border: none;
	}
`;

const renderDividers = number => {
	const dividers: ReactNode[] = [];
	for (let i = 0; i < number; i++) {
		dividers.push(<Divider orientation="vertical" style={{ position: 'absolute', top: 0, bottom: 0, left: `${1 + i * 1.5}rem` }} />);
	}
	return <>{dividers.map(d => d)}</>;
};

const TreeSelectItem = ({
	option,
	searchValue,
	handleDropdownClick,
	onClick,
	disabled,
	inGridCell,
	...rest
}: {
	option: TreeSelectOption;
	searchValue: string;
	onClick: (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
	handleDropdownClick: () => void;
	disabled: boolean;
	inGridCell?: boolean;
}) => {
	const hasChildren = !!option?.props?.children?.length;
	const showArrow = hasChildren && !searchValue;
	const showLine = !!searchValue;
	const isCategory = !option.props.selectable && hasChildren;
	return (
		<MuiMenuItem
			{...rest}
			onClick={onClick}
			disabled={disabled}
			style={{
				paddingLeft: `${showArrow ? 1 + option.nestLevel * 1.5 : showLine ? 1 + option.nestLevel * 1.5 : 3.25 + option.nestLevel * 1.5}rem`,
				display: 'flex',
				alignItems: 'center'
			}}
		>
			{showArrow ? (
				<ListItemIcon>
					<IconButton
						onClick={e => {
							handleDropdownClick();
							e.stopPropagation();
						}}
						iconName={option.collapsed ? 'ArrowDropDown' : 'ArrowDropUp'}
						size="small"
					/>
				</ListItemIcon>
			) : null}
			{option.nestLevel === 0 && option.props.colour ? (
				<div
					style={{
						flexShrink: 0,
						marginRight: 7,
						width: 14,
						height: 14,
						borderRadius: '50%',
						backgroundColor: option.props.colour,
						border: `1px solid ${option.props.colour}`
					}}
				/>
			) : null}
			<FlexColumn>
				<FlexRowCenterAligned>
					<ListItemText
						// you would think that you shouldn't need to set whiteSpace to 'normal' given that it's the default... but without this,
						// the options in the popover get cut off in the table cells.
						style={inGridCell ? { flex: 1, whiteSpace: 'normal', wordBreak: 'break-word' } : { textOverflow: 'ellipsis', flex: 1 }}
						primary={<HighlightedText text={option.props.label} searchTerm={searchValue} />}
					/>
					{showLine ? renderDividers(option.nestLevel) : null}
				</FlexRowCenterAligned>
				{isCategory && !showLine && option.props.categoryActionElement ? option.props.categoryActionElement : null}
			</FlexColumn>
		</MuiMenuItem>
	);
};

function childHasLabel(tree: TreeSelectItemProps[], targetLabel: string): boolean {
	if (!tree) return false;
	for (const item of tree) {
		if (item.label?.toLowerCase().includes(targetLabel?.toLowerCase())) {
			return true;
		}
		if (item.children?.length) {
			if (childHasLabel(item.children, targetLabel)) {
				return true;
			}
		}
	}
	return false;
}

function flattenOptions(
	items: TreeSelectItemProps[],
	flattened: TreeSelectItemProps[] = [],
	parentIndex?: number,
	level?: number
): TreeSelectOption[] {
	const nestLevel = level ? level : 0;
	let result: TreeSelectOption[] = [];
	for (const item of items) {
		result.push({
			props: item,
			parentIndex,
			nestLevel,
			collapsed: true,
			value: item.value
		});
		if (item?.children?.length) {
			// 'parentIndex + 1' because we are definitely at least 1 item past this items parent
			// (but if it's a top level item then parentIndex is undefined, so 0).
			// We can then find out the new parentIndex by adding on how many results we've already pushed to array in this function call
			//  i.e. result.length... and finally '- 1' because 0 indexed.
			const newParentIndex = (parentIndex !== undefined ? parentIndex + 1 : 0) + result.length - 1;
			result = result.concat(flattenOptions(item.children, flattened, newParentIndex, nestLevel + 1));
		}
	}
	return result;
}

/**
 * A helper function to filter options for TreeSelect.
 * This ensures only selected options are visible for non multiple TreeSelect when clicked/opened
 * For multiple, there is no search value when chips are selected so doesn't filter on open.
 * @param options - Options to filter
 * @param searchValue - Search string to filter by
 * @returns
 */
function filterOptions(options: TreeSelectOption[], searchValue: string) {
	return options.filter(
		option =>
			option.props.label.toLowerCase().includes(searchValue.toLowerCase()) ||
			(option.props.children && childHasLabel(option.props.children, searchValue))
	);
}

/**
 * TreeSelect is similar to the Select but allows nested options to be rendered in collapsible menus, and filtered using the input.
 * Items at any level can be selectable or not.
 */
export function TreeSelect<Multiple extends boolean | undefined = undefined, DisableClearable extends boolean | undefined = undefined>({
	label,
	status,
	helperText,
	variant,
	startAdornment,
	options,
	required,
	getOptionLabel,
	renderTags: externalRenderTags,
	...rest
}: TreeSelectProps<Multiple, DisableClearable, false>) {
	const resolvedGetOptionLabel = getOptionLabel ?? ((o: TreeSelectOption) => o.props.label);
	const { contextValue, isOpen, onOpen, onClose } = useMuiClickToDismiss(rest.open, rest.onClose, rest.onOpen);
	const [searchValue, setSearchValue] = useState('');
	const container = useContext(ContainerContext);
	const inGridCell = variant === 'cell';

	const [localOptions, setLocalOptions] = useState(flattenOptions(options, []));

	const handleDropDownClick = (options: TreeSelectOption[], index: number, forceCollapse?: boolean): TreeSelectOption[] => {
		let newOptions = Array.from(options);
		const targetOption = newOptions[index];
		// if forceCollapse: collapse the target option. Otherwise: toggle it.
		newOptions[index].collapsed = forceCollapse ?? !targetOption.collapsed;
		// if target option has children, and it has been collapsed: collapse children also.
		if (targetOption.props.children?.length && newOptions[index].collapsed) {
			// `i = index` because children will never come before parent.
			for (let i = index + 1; i < newOptions.length; i++) {
				// if the item is child of targetOption: recursively call function with forceCollapse true.
				if (newOptions[i].parentIndex === index) {
					newOptions = handleDropDownClick(newOptions, i, true);
				}
			}
		}
		return newOptions;
	};

	const TextComp = inGridCell ? GridCellTextField : StyledTextField;

	return (
		<ClickToDismissPortalContext.Provider value={contextValue}>
			<MuiAutocomplete<TreeSelectOption, Multiple, DisableClearable, false>
				{...rest}
				open={isOpen}
				onClose={onClose}
				onOpen={onOpen}
				getOptionLabel={resolvedGetOptionLabel}
				renderTags={
					externalRenderTags ??
					((value, getTagProps) => {
						// if props is null, it means it is a value that doesn't exist
						return value
							.filter(a => Boolean(a.props))
							.map((option, index) => {
								const label = resolvedGetOptionLabel(option);
								return (
									<Chip
										label={label}
										title={label}
										{...getTagProps({ index })}
										size="small"
										key={`${option.value}_${option.props.label}`}
									/>
								);
							});
					})
				}
				options={filterOptions(localOptions, searchValue)}
				isOptionEqualToValue={(option, value) => option?.value === value?.value}
				filterOptions={(options, { inputValue }) => filterOptions(options, inputValue)} // Requires special filtering logic for nesting
				renderOption={(props, option) => {
					const index = props['data-option-index'] as number;
					const parentIndex = option.parentIndex;
					const { onClick, ...rest } = props;
					return parentIndex !== undefined && localOptions?.[parentIndex]?.collapsed ? null : (
						<TreeSelectItem
							{...rest}
							disabled={!option.props.selectable && !!searchValue}
							onClick={e => (option.props.selectable ? onClick?.(e) : setLocalOptions(prev => handleDropDownClick(prev, index)))}
							key={option.props.label + option.parentIndex + option.props.value}
							option={option}
							searchValue={searchValue}
							handleDropdownClick={() => setLocalOptions(prev => handleDropDownClick(prev, index))}
							inGridCell={inGridCell}
						/>
					);
				}}
				forcePopupIcon={rest.forcePopupIcon}
				componentsProps={{ popper: { container: container ?? undefined } }}
				onInputChange={(_event, value) => {
					setSearchValue(value);
					const hasSearchValue = value !== '';

					if (!hasSearchValue) {
						setLocalOptions(prev =>
							prev.map(option => {
								return { ...option, collapsed: true };
							})
						);
						// When searching, expand all options
					} else {
						setLocalOptions(prev =>
							prev.map(option => {
								return { ...option, collapsed: false };
							})
						);
					}
				}}
				renderInput={inputProps => (
					<TextComp
						{...inputProps}
						required={required}
						status={status === 'loading' ? undefined : status}
						hideValidationIcon
						startAdornment={startAdornment ?? inputProps.InputProps.startAdornment}
						helperText={inGridCell ? null : helperText}
						InputProps={{
							...inputProps.InputProps,
							endAdornment: mergeEndAdornments(inputProps.InputProps.endAdornment, status, null),
							style: inGridCell ? { flexWrap: 'nowrap' } : undefined
						}}
						label={inGridCell ? undefined : label}
					/>
				)}
			/>
		</ClickToDismissPortalContext.Provider>
	);
}
